import { debounceTime, filter, switchMap, take } from 'rxjs/operators';
import { AfterViewInit, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin } from 'rxjs';

import animateScrollTo from 'animated-scroll-to';
import { ToastrService } from 'ngx-toastr';

import {
  CartService,
  CurrencyService,
  CustomerService,
  EventsService,
  FaviconService,
  MetaService,
  ProductionService,
  StoreService,
  ZendeskService,
  isObjectEmpty,
  WishListApiService,
  RouterService,
  CountryService,
  CookieConsentService,
  GoogleAnalyticsService,
} from '@speedy/core';

@Component({
  selector: 'app-speedy',
  standalone: true,
  imports: [RouterOutlet],
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly countryService = inject(CountryService);
  private readonly customerService = inject(CustomerService);
  private readonly cartService = inject(CartService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly metaService = inject(MetaService);
  private readonly storeService = inject(StoreService);
  private readonly eventsService = inject(EventsService);
  private readonly productionService = inject(ProductionService);
  private readonly faviconService = inject(FaviconService);
  private readonly zenddeskService = inject(ZendeskService);
  private readonly currencyService = inject(CurrencyService);
  private readonly wishListApiService = inject(WishListApiService);
  private readonly alertService = inject(ToastrService);
  private readonly routerService = inject(RouterService);
  private readonly googleAnalyticsService = inject(GoogleAnalyticsService);
  private readonly cookiesService = inject(CookieConsentService);

  ngOnInit() {
    this.setMetaData();

    if (this.productionService.isServerMode()) return;

    this.initCurrencies();
    this.cookiesService.init();
    this.googleAnalyticsService.init();
    this.countryService.getCountries().pipe(take(1)).subscribe();

    this.subscribeToNavigationEnd();
    this.subscribeToUrlFragment();
    this.subscribeToCustomer();
    this.initCart();
  }

  ngAfterViewInit(): void {
    this.zenddeskService.init();
    this.bindLinksWithHash();
  }

  private subscribeToCustomer() {
    this.customerService
      .subscribeToCustomer()
      .pipe(
        debounceTime(100),
        filter((c) => !!c && !isObjectEmpty(c)),
        switchMap(() => this.addSavedItemsToWishlist()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.wishListApiService.removeExistingStoredIds();
          this.routerService.navigate('/customer/wishlist');
          this.alertService.success('Saved product/s were added to wishlist');
        },
        error: (error) => {
          this.wishListApiService.removeExistingStoredIds();
          this.alertService.error(error.error?.message ?? error.error);
        },
      });
  }

  private addSavedItemsToWishlist() {
    const items = this.wishListApiService.getExistingStoredIds();

    return forkJoin(items.map((item) => this.wishListApiService.addToWishList(item)));
  }

  private subscribeToUrlFragment() {
    this.activatedRoute.fragment
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((fragment) => {
        this.scrollToFragment(fragment as string);
      });
  }

  private subscribeToNavigationEnd() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event: NavigationEnd | any) => {
        this.redirectHome(event);
        this.eventsService.broadcast('route_changed', event);
        this.eventsService.broadcast('page_view', event);
      });
  }

  private initCart() {
    this.customerService
      .initSession()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.cartService.initCart();
        },
        error: () => {
          this.cartService.initCart();
        },
      });
  }

  private initCurrencies() {
    this.currencyService.getCurrencies().pipe(take(1)).subscribe();
    this.currencyService.getCurrentCurrency().pipe(take(1)).subscribe();
  }

  private redirectHome(event: NavigationEnd) {
    const urlSplit = event['url'].split('?');
    if (urlSplit[0] === '/') {
      const store = this.storeService.getCurrentStore();
      this.router.navigate(['/' + store?.code]);
    }
  }

  private setMetaData() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        const root: ActivatedRoute = this.router.routerState.root;
        this.metaService.setMetaDataRecursive(root);
        this.faviconService.setFavicon();
      });
  }

  private scrollToFragment(fragment: string) {
    if (fragment?.length) {
      setTimeout(() => {
        const element = document.querySelector('#' + fragment);
        if (element) {
          const elemOffset = element.getBoundingClientRect().top + window.scrollY - 45;
          animateScrollTo(elemOffset);
        }
      }, 0);
    }
  }

  private bindLinksWithHash() {
    if (this.productionService.isBrowserMode()) {
      setTimeout(() => {
        const links = document.querySelectorAll(
          'a[href]:not([href="#"]):not([href="#0"]):not(.media):not(.nav-link):not(.icon-wrap)',
        );
        links.forEach((link: any | Element) => {
          const maps = link.pathname.split('/')[1];
          if (maps !== 'maps') {
            link.addEventListener('click', (e: Event) => {
              e.preventDefault();
              if (link.hash) {
                this.router.navigate([link.pathname], {
                  fragment: link.hash.replace('#', ''),
                });
              } else if (link.pathname) {
                const params =
                  link.href.split('?').length > 1 ? `?${link.href.split('?')[1]}` : '';
                this.router.navigateByUrl(link.pathname + params);
              }
            });
          }
        });
      }, 350);
    }
  }
}
